import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Organization } from '@nargus/shared';
import { Check, Copy, LoaderCircle, Share2, Trash2 } from 'lucide-react';
import {
  useCreateShare,
  useDeleteShare,
  useShares,
} from '@/resources/share/share.service';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyNote, ErrorNote, formatDate } from './ui';

const NEVER = '0';
const DURATIONS = ['0', '1', '7', '30', '365'];

export function shareUrl(token: string): string {
  return `${window.location.origin}/share/${token}`;
}

export function PhotoShareDialog({
  organization,
  photoIds,
  albumId,
  onClose,
}: {
  organization: Organization;
  photoIds?: string[];
  albumId?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [expiry, setExpiry] = useState(NEVER);
  const [created, setCreated] = useState<string | null>(null);
  const shares = useShares(organization.id);
  const create = useCreateShare();
  const remove = useDeleteShare();

  const labels: Record<string, string> = {
    '0': t('shares.never'),
    '1': t('shares.days', { count: 1 }),
    '7': t('shares.days', { count: 7 }),
    '30': t('shares.days', { count: 30 }),
    '365': t('shares.days', { count: 365 }),
  };

  const count = albumId ? undefined : (photoIds?.length ?? 0);
  const canCreate = Boolean(albumId) || (count ?? 0) > 0;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('shares.title')}</DialogTitle>
          <DialogDescription>
            {albumId
              ? t('shares.hintAlbum')
              : t('shares.hintSelection', { count: count ?? 0 })}
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <Field>
            <FieldLabel htmlFor="share-link">{t('shares.linkLabel')}</FieldLabel>
            <div className="flex gap-2">
              <Input id="share-link" readOnly value={shareUrl(created)} />
              <CopyButton value={shareUrl(created)} />
            </div>
          </Field>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <Field className="min-w-40 flex-1">
              <FieldLabel htmlFor="share-expiry">{t('shares.expiryLabel')}</FieldLabel>
              <Select
                items={labels}
                value={expiry}
                onValueChange={(value) => setExpiry(String(value ?? NEVER))}
              >
                <SelectTrigger id="share-expiry" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {labels[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Button
              type="button"
              disabled={!canCreate || create.isPending}
              onClick={() =>
                create.mutate(
                  {
                    organizationId: organization.id,
                    albumId,
                    photoIds: albumId ? undefined : photoIds,
                    expiresInDays: expiry === NEVER ? undefined : Number(expiry),
                  },
                  { onSuccess: (data) => setCreated(data.share.token) },
                )
              }
            >
              {create.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Share2 />
              )}
              {t('shares.create')}
            </Button>
          </div>
        )}

        <ErrorNote error={create.error ?? remove.error} />

        <div className="flex flex-col gap-1 border-t pt-3">
          <p className="text-sm font-medium">{t('shares.existing')}</p>

          {shares.isPending ? (
            <Skeleton className="h-9 w-full" />
          ) : shares.data?.length === 0 ? (
            <EmptyNote>{t('shares.none')}</EmptyNote>
          ) : (
            <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
              {shares.data?.map((share) => (
                <li key={share.id} className="flex items-center gap-2 py-1">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">
                      {share.albumName ??
                        t('shares.photoCount', { count: share.photoCount })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {share.expiresAt
                        ? t('shares.until', { date: formatDate(share.expiresAt) })
                        : t('shares.never')}
                    </p>
                  </div>
                  <CopyButton value={shareUrl(share.token)} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('shares.revoke')}
                    disabled={remove.isPending}
                    onClick={() =>
                      remove.mutate({
                        organizationId: organization.id,
                        shareId: share.id,
                      })
                    }
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      aria-label={t('shares.copy')}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check /> : <Copy />}
    </Button>
  );
}
